import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { suggestSearch } from "@/lib/api";

export default function SearchDialog({ open, onClose }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const inputRef = useRef();

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else { setQ(""); setSuggestions([]); }
  }, [open]);

  useEffect(() => {
    if (!q.trim()) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const data = await suggestSearch(q);
        setSuggestions(data.hits || []);
      } catch { /* suggestion failures are non-critical */ }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const submit = (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    nav(`/search?q=${encodeURIComponent(q.trim())}`);
    onClose?.();
  };

  const onKey = (e) => { if (e.key === "Escape") onClose?.(); };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center" onKeyDown={onKey} data-testid="search-dialog">
      <div className="absolute inset-0" style={{ background: "rgba(20,20,20,0.55)" }} onClick={onClose} />
      <div className="relative w-full max-w-2xl mx-4 mt-24 bg-white border shadow-xl" style={{ borderColor: "var(--gi-border)" }}>
        <form onSubmit={submit} className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: "var(--gi-border)" }}>
          <Search size={18} style={{ color: "var(--gi-maroon)" }} />
          <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} placeholder="Search books, articles, research, events…" className="flex-1 py-2 outline-none text-lg" data-testid="search-dialog-input" />
          <button type="button" onClick={onClose} aria-label="Close" className="p-1" data-testid="search-dialog-close"><X size={18} /></button>
        </form>
        {suggestions.length > 0 && (
          <div className="max-h-[60vh] overflow-y-auto" data-testid="search-dialog-suggestions">
            {suggestions.map((s, i) => (
              <button key={`${s.type}-${s.url}-${i}`} className="w-full text-left px-5 py-3 border-b hover:bg-[var(--gi-ivory-2)]" style={{ borderColor: "var(--gi-divider)" }} onClick={() => { nav(s.url); onClose?.(); }} data-testid={`search-suggestion-${i}`}>
                <div className="font-accent text-[9px]" style={{ color: "var(--gi-gold)", letterSpacing: "0.18em" }}>{s.type}{s.series ? ` · ${s.series}` : ""}</div>
                <div className="mt-1 font-heading text-base" style={{ color: "var(--gi-charcoal)" }}>{s.title}</div>
              </button>
            ))}
          </div>
        )}
        <div className="px-5 py-3 text-xs flex items-center justify-between" style={{ color: "var(--gi-muted)" }}>
          <span>Press Enter for full results</span>
          <span>Esc to close</span>
        </div>
      </div>
    </div>
  );
}
